/**
 * Per-room floor/ceiling detection over the room's WHOLE polygon.
 *
 * Replaces the old single-spot ray probe (a ≤1.2m patch of rays at the
 * bounding-box centroid, where any shelf or beam under that one spot won the
 * median and set a wrong room base/ceiling). This runs the same column
 * z-histogram → surfaces → intervals → patches pipeline the storey detection
 * uses, restricted to the columns inside the room polygon, and picks the
 * patch that covers the largest share of it — the region where a floor AND a
 * matching ceiling actually pair up.
 *
 * Detection supplies HEIGHTS only. The polygon is user input and is never
 * reshaped by this module.
 */

import * as THREE from 'three';
import type { PointCloudOctree } from '../../PointCloudOctree';
import { resolveTuning } from './tuning';
import type { ColumnInterval, RoomPatch } from './types';
import { accumulateNode, collectNodesInBox, createColumnGrid } from './columnHistogram';
import { extractColumnSurfaces } from './surfaces';
import { extractColumnIntervals } from './intervals';
import { consensusFilterIntervals } from './roleConsensus';
import { buildRoomPatches } from './roomPatches';

export interface RoomVolumeDetection {
  floorZ: number;
  ceilingZ: number;
  /** Fraction of the polygon's area the winning patch covers (clamped to 1). */
  coverage: number;
}

/** Patch must cover at least this fraction of the polygon to be trusted. */
const MIN_PATCH_COVERAGE = 0.15;
/** Histogram z-slack beyond the expected band — context for the surface
 *  extraction near the band edges. */
const SEARCH_SLACK_M = 0.75;
/**
 * A column interval may start below the expected floor / end above the
 * expected ceiling by at most this much. Kept smaller than the thinnest
 * plausible slab, so where a column's real floor or ceiling is missing (scan
 * shadow, slab hole) the adjacent storey's surface — the slab UNDERSIDE
 * below, or the floor above — can never be picked as this room's bound.
 * Deviation INTO the band (raised floors, dropped ceilings) stays free.
 */
const BAND_TOLERANCE_M = 0.15;

/**
 * Detect the room's floor/ceiling heights. `expectedFloorZ` /
 * `expectedCeilingZ` are the floor group's planes — the search band. Returns
 * null when no plausible floor+ceiling patch covers enough of the polygon
 * (caller falls back to the floor-group planes).
 */
export function detectRoomVolume(pointclouds: PointCloudOctree[], polygon: THREE.Vector2[], expectedFloorZ: number, expectedCeilingZ: number): RoomVolumeDetection | null {
  if (polygon.length < 3 || !(expectedCeilingZ > expectedFloorZ) || pointclouds.length === 0) return null;
  const tuning = resolveTuning();
  const zMin = expectedFloorZ - SEARCH_SLACK_M;
  const zMax = expectedCeilingZ + SEARCH_SLACK_M;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of polygon) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const box = new THREE.Box3(new THREE.Vector3(minX, minY, zMin), new THREE.Vector3(maxX, maxY, zMax));

  const grid = createColumnGrid(box, tuning);
  for (const node of collectNodesInBox(pointclouds, box)) {
    accumulateNode(grid, node.positions, node.count, node.matrixWorld, box, null);
  }
  if (grid.totalPoints === 0) return null;

  // Surfaces + intervals for the columns whose cell centre lies in the polygon.
  const intervalsByColumn: Array<ColumnInterval[] | null> = new Array<ColumnInterval[] | null>(grid.columns.length).fill(null);
  for (let column = 0; column < grid.columns.length; column++) {
    const bins = grid.columns[column];
    if (!bins) continue;
    const cx = grid.originX + ((column % grid.nx) + 0.5) * grid.cellSize;
    const cy = grid.originY + (Math.floor(column / grid.nx) + 0.5) * grid.cellSize;
    if (!pointInPolygon(cx, cy, polygon)) continue;
    const surfaces = extractColumnSurfaces(bins, grid.binSize, grid.originZ, tuning);
    if (surfaces.length === 0) continue;
    const { intervals } = extractColumnIntervals(surfaces, bins, grid.binSize, grid.originZ, tuning);
    // Band filter: drop intervals reaching outside the expected storey — an
    // interval starting at the slab underside or ending at the floor above
    // would drag the whole patch's trimmed mean out of the room.
    const banded = intervals.filter((iv) => iv.floorZ >= expectedFloorZ - BAND_TOLERANCE_M && iv.ceilingZ <= expectedCeilingZ + BAND_TOLERANCE_M);
    if (banded.length > 0) intervalsByColumn[column] = banded;
  }

  // Neighbourhood role consensus: drop intervals whose endpoint sits on a
  // level most neighbouring columns assign the OPPOSITE role (mispairs
  // through scan shadows — see roleConsensus.ts).
  const { filtered: consensusIntervals } = consensusFilterIntervals(intervalsByColumn, grid.nx, grid.ny);

  const patches = buildRoomPatches(consensusIntervals, grid.nx, grid.ny, grid.cellSize, grid.originX, grid.originY, tuning);
  if (patches.length === 0) return null;

  const polygonArea = Math.abs(ringSignedArea(polygon));
  if (polygonArea < 1e-6) return null;

  let best: RoomPatch | null = null;
  let bestCoverage = 0;
  for (const patch of patches) {
    const coverage = Math.min(1, (patch.cellCount * grid.cellSize * grid.cellSize) / polygonArea);
    if (coverage > bestCoverage) {
      bestCoverage = coverage;
      best = patch;
    }
  }
  if (!best || bestCoverage < MIN_PATCH_COVERAGE) return null;

  // Re-estimate the heights median-anchored over the patch's member columns.
  // The patch's own trimmed mean (10% per side) cannot reject the 20-30%
  // minority of columns that saw the slab UNDERSIDE (scan shadow under
  // furniture with points below the slab) — the median lands on the dominant
  // walkable surface, and the anchored mean then averages only the samples
  // that agree with it.
  const floorSamples: number[] = [];
  const ceilingSamples: number[] = [];
  for (const cell of best.cells) {
    const intervals = consensusIntervals[cell];
    if (!intervals || intervals.length === 0) continue;
    let bestInterval = intervals[0];
    let bestOverlap = -Infinity;
    for (const iv of intervals) {
      const overlap = Math.min(iv.ceilingZ, best.ceilingZ) - Math.max(iv.floorZ, best.floorZ);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestInterval = iv;
      }
    }
    floorSamples.push(bestInterval.floorZ);
    ceilingSamples.push(bestInterval.ceilingZ);
  }
  const floorZ = floorSamples.length > 0 ? medianAnchoredMean(floorSamples) : best.floorZ;
  const ceilingZ = ceilingSamples.length > 0 ? medianAnchoredMean(ceilingSamples) : best.ceilingZ;

  return { floorZ, ceilingZ, coverage: bestCoverage };
}

/** Samples within this distance of the median vote on the final height. */
const MEDIAN_ANCHOR_WINDOW_M = 0.1;

/**
 * Mean of the samples within {@link MEDIAN_ANCHOR_WINDOW_M} of the median.
 * Robust against a bimodal split (dominant surface + a sizeable minority on
 * the wrong side of the slab): any minority below 50% cannot move the median,
 * so its samples fall outside the anchor window entirely.
 */
export function medianAnchoredMean(samples: number[]): number {
  const sorted = [...samples].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  let sum = 0;
  let count = 0;
  for (const s of sorted) {
    if (Math.abs(s - median) <= MEDIAN_ANCHOR_WINDOW_M) {
      sum += s;
      count++;
    }
  }
  return count > 0 ? sum / count : median;
}

function ringSignedArea(ring: THREE.Vector2[]): number {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
}

function pointInPolygon(x: number, y: number, polygon: THREE.Vector2[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
